<template>
    <div
        id="lt-yantr"
        class="vp-mortgage-payment-lite">
        <div class="container-width">
            <div class="filter-section">
                <h2 class="title">
                    <img src="./images/home-icon.svg" /> How much will your monthly property expense be?
                </h2>
                <ul class="filter-block">
                    <li>
                        <h5>Property Details</h5>
                        <ul class="filter-inputs">
                            <li>
                                <number-input
                                    v-model="HomePrice"
                                    :label="labelHomePrice"
                                    inputmode="decimal"
                                    @input="updateHomeInsurance($event)"
                                />
                            </li>
                            <li class="percentage-amt">
                                <number-input
                                    v-model="DownPayment"
                                    :label="labelDownPayment"
                                    inputmode="decimal"
                                    @input="DownPaymentChange($event)"
                                />
                                <span class="percentage-val">{{ DownPaymentPercent }}%</span>
                            </li>
                            <li class="include-pmi">
                                <div class="form-checkbox">
                                    <input
                                        id="includePMI"
                                        v-model="IsPmiInclude"
                                        type="checkbox"
                                        :disabled="CheckPMI==false"
                                    />
                                    <label for="includePMI">Include PMI</label>
                                    <lt-label
                                        show-tooltip="true"
                                        :show-question-mark="ShowQuestionMark"
                                        :popper-content="PopperContent"
                                        :span="span"
                                        :tooltip-link="PrivateMortgageInsuranceTooltip"
                                        @showPopup="showPopup"
                                    />
                                </div>
                            </li>
                        </ul>
                    </li>
                    <li>
                        <h5>Mortgage Details</h5>
                        <ul class="filter-inputs">
                            <li>
                                <number-input
                                    v-model="MortgageRate"
                                    max-value="100.000"
                                    precision="3"
                                    symbol="%"
                                    symbol-aligned="right"
                                    inputmode="decimal"
                                    :label="labelMortgageRate"
                                    @input="restrictMortgageRateToMaxValue"
                                />
                            </li>
                            <li>
                                <drop-down
                                    v-model="LoanTermInYears"
                                    :label="labelLoanType"
                                    :options="LoanTermInYearsList"
                                    @input="updateMortgageRate($event)"
                                />
                            </li>
                        </ul>
                    </li>
                    <li>
                        <h5
                            class="other-expenses"
                            @click="viewExpenses()">
                            Other Expenses
                        </h5>
                        <ul
                            class="filter-inputs"
                            :class="{'hide-expenses': ViewExpenses}">
                            <li class="percentage-amt inputs-subtext">
                                <number-input
                                    v-model="PropertyTaxes"
                                    :label="labelPropertyTaxes"
                                    inputmode="decimal"
                                    @input="PropertyTaxesChange($event)"
                                />
                            </li>
                            <li class="inputs-subtext">
                                <number-input
                                    v-model="HomeInsurance"
                                    :label="labelHomeInsurance"
                                    inputmode="decimal"
                                />
                            </li>
                            <li class="inputs-subtext">
                                <number-input
                                    v-model="HoaFees"
                                    :label="labelHoaFees"
                                    inputmode="decimal"
                                    maxlength="6"
                                />
                            </li>
                        </ul>
                    </li>
                </ul>
            </div>

            <div class="calculation-section">
                <h2 class="title">
                    Your {{ PaymentFrequency }} payment will be
                    <b
                        class="homeprice"
                        :class="{ 'red-text': !isNegativeAmount}">${{ currencyFilterWithPrecision(TotalMoPayment) }}</b>.
                </h2>
                <div class="graph-block">
                    <div class="left-section">
                        <div>
                            <div class="chart-parent">
                                <vue-chart
                                    type="doughnut"
                                    :data="chartData"
                                    :options="options"
                                    :width="chartWidth"
                                    :height="chartHeight"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="right-section">
                        <ul class="summary">
                            <li>
                                <label class="blue-text">Principal & Interest</label>
                                <span
                                    class="value"
                                    :class="{ 'red-text': !isNegativeAmount}">${{ currencyFilterWithPrecision(MoPrincipalIntestrest/Math.round(PayFreqInNumber/12)) }}</span>
                            </li>
                            <li>
                                <label class="green-text">Property Taxes</label>
                                <span class="value">${{ currencyFilterWithPrecision(PropertyTaxesCurrency*12/PayFreqInNumber) }}</span>
                            </li>
                            <li>
                                <label class="dark-green-text">Homeowner's Insurance</label>
                                <span class="value">${{ currencyFilterWithPrecision(HomeInsuranceCurrency*12/PayFreqInNumber) }}</span>
                            </li>
                            <li>
                                <label class="ocean-blue-text">HOA Fees</label>
                                <span class="value">${{ currencyFilterWithPrecision(HoaFees*12/PayFreqInNumber) }}</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <ul class="total-value">
                <li>
                    <i class="lt4-Mortgage"></i>
                    <div>
                        <label>Total Loan Amount</label>
                        <span>${{ currencyFilterWithPrecision(totalLoanAmount) }}</span>
                    </div>
                </li>

                <li>
                    <i class="lt4-Percent"></i>
                    <div>
                        <label>Total Interest Paid</label>
                        <span>${{ currencyFilterWithPrecision(AllTotalInterest) }}</span>
                    </div>
                </li>
                <li>
                    <i class="lt4-DollarSign"></i>
                    <div>
                        <label>Total All Payment</label>
                        <span>${{ currencyFilterWithPrecision(allTotalPayment) }}</span>
                    </div>
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
    import VueChart from 'vue-chart-js';
    import Accounting from 'accounting';
    import NumberInput from '~/components/number-input.vue';
    import DropDown from '~/components/drop-down.vue';
    import DefaultTooltip from '~/constants/tooltip-defaults';
    import LoanTermList from '~/constants/loan-term-list';
    import LtLabel from '~/components/lt-label.vue';
    import ChartTooltip from '~/helpers/chart-tooltip';
    import PaymentHelper from '~/helpers/payment';
    import TokenService from '~/services/token';
    import AmortizationBreakdownHelper from '~/helpers/mortgage-amortization-breakdown';

    export default {
        components: {
            NumberInput,
            DropDown,
            LtLabel,
            VueChart,
        },
        props: {
            labelHomePrice: {
                default: 'Home Price',
            },
            homePrice: {
                default: '350000',
            },
            labelDownPayment: {
                default: 'Down Payment',
            },
            downPayment: {
                default: '70000',
            },
            labelMortgageRate: {
                default: 'Mortgage Rate',
            },
            mortgageRate: {
                default: '3.630',
            },
            labelLoanType: {
                default: 'Loan Type',
            },
            loanTermInYears: {
                default: 30,
            },
            labelPropertyTaxes: {
                default: 'Annual Property Taxes',
            },
            propertyTaxes: {
                default: '1.25',
            },
            labelHomeInsurance: {
                default: 'Annual Home Insurance',
            },
            homeInsurance: {
                default: '1225',
            },
            labelHoaFees: {
                default: 'Monthly HOA Fees',
            },
            hoaFees: {
                default: '50',
            },
            checkPMI: {
                default: false,
            },
            privateMortgageInsuranceTooltip: {
                default: DefaultTooltip.PrivateMortgageInsurance,
            },
            span: {
                type: String,
                default: '',
            },
            maxValue: {
                default: 999999999,
            },
            oneTimeExpenseRate: {
                default: '3',
            },
            pmiPerc: {
                default: '0.00625',
            },
            moExtraPayment: {
                default: '0',
            },
            totalMoPayment: {
                default: '2083',
            },
            homeInsurancePerc: {
                default: '0.35',
            },
            mortgageCalcUrl: {
                default: '/mortgage-calculator',
            },
            downPaymentPercent: {
                default: 20.00,
            },
            propertyTaxesPercent: {
                default: 1.25,
            },
            showQuestionMark: {
                default: true,
            },
            popperContent: {
                default: '',
            },
        },
        data() {
            return {
                HomePrice: this.homePrice,
                DownPayment: this.downPayment,
                MortgageRate: parseFloat(this.mortgageRate),
                PropertyTaxes: parseFloat(this.propertyTaxes),
                HomeInsurance: this.homeInsurance,
                HoaFees: parseInt(this.hoaFees, 10),
                LoanTermInYears: this.loanTermInYears,
                LoanTermInYearsList: LoanTermList.LoanTermInYearsList.refinance,
                PrivateMortgageInsuranceTooltip: parseInt(this.hideTooltip, 10) ? '' : this.privateMortgageInsuranceTooltip,
                TotalInterestTooltip: parseInt(this.hideTooltip, 10) ? '' : this.totalInterestTooltip,
                DownPaymentPercent: Accounting.formatNumber(this.downPaymentPercent, { precision: 2 }),
                PropertyTaxesPercent: this.propertyTaxesPercent,
                CheckPMI: this.checkPMI,
                IsPmiInclude: this.checkPMI,
                PmiPerc: parseFloat(this.pmiPerc),
                MoPrincipalIntestrest: this.moPrincipalIntestrest,
                PropertyTaxesCurrency: this.propertyTaxesCurrency,
                HomeInsuranceCurrency: this.homeInsuranceCurrency,
                PmiCurrency: this.pmiCurrency,
                TotalMoPayment: this.totalMoPayment,
                MoExtraPayment: parseInt(this.moExtraPayment, 10),
                HomeInsurancePerc: parseFloat(this.homeInsurancePerc),
                MonthlyInterestRatePerc: this.monthlyInterestRatePerc,
                MortgageAmount: this.mortgageAmount,
                OneTimeExpenses: this.oneTimeExpenses,
                DownPaymentExpenses: this.downPaymentExpenses,
                PmiAmount: this.pmiAmount,
                InitialPropertyTax: this.PropertyTaxes,
                InitialPropertyTaxPercent: this.PropertyTaxesPercent,
                InitialDownPayment: this.DownPayment,
                InitialDownPaymentPercent: this.DownPaymentPercent,
                InitialHomePrice: this.homePrice,
                ViewExpenses: true,
                // Setting for chart
                chartData: {
                    labels: [],
                    datasets: [
                        {
                            data: [],
                            backgroundColor: [
                                '#1e8ced',
                                '#84bd00',
                                '#007864',
                                '#00c3ae',
                            ],
                            borderWidth: 0,
                        },
                    ],
                },
                options: {
                    cutoutPercentage: 0,
                    legend: {
                        display: false,
                    },
                    tooltips: {
                        enabled: false,
                        custom: ChartTooltip.customTooltips,
                    },
                },
                chartWidth: 160,
                chartHeight: 160,
                PaymentFrequency: 'monthly',
                ShowModal: false,
                ShowQuestionMark: this.showQuestionMark,
                PopperContent: this.popperContent,
                CurrentDate: new Date(),
                AllTotalPrincipal: 0,
                AllTotalInterest: 0,
                AllTotalTaxesFees: 0,
                AllTotalExtraPayments: 0,
            };
        },
        computed: {
            isNegativeAmount() {
                return this.TotalMoPayment >= 0;
            },
            getFieldValue() {
                return this.unformat(this.HomePrice) + this.DownPayment + this.LoanTermInYears + this.MortgageRate + this.PropertyTaxes + this.HomeInsurance + this.HoaFees + this.IsPmiInclude + this.MoExtraPayment + this.PaymentFrequency;
            },
            PayFreqInNumber() {
                return this.PaymentFrequency === 'bi-weekly'
                    ? 26
                    : this.PaymentFrequency === 'weekly'
                        ? 52
                        : 12;
            },
            totalLoanAmount() {
                return this.unformat(this.HomePrice) - this.unformat(this.DownPayment);
            },
            allTotalPayment() {
                return this.DownPaymentExpenses + this.AllTotalPrincipal + this.AllTotalInterest + this.AllTotalTaxesFees + this.AllTotalExtraPayments;
            },
            calculateTaxesFees() {
                return this.PropertyTaxesCurrency + this.HomeInsuranceCurrency + parseFloat(this.HoaFees);
            },
        },
        watch: {
            getFieldValue() {
                this.calculatePayment(this.MortgageRate);
            },
            CheckPMI() {
                if (this.CheckPMI === true) this.IsPmiInclude = true;
            },
            LoanTermInYears() {
                this.updateTableCalculation();
            },
        },
        mounted() {
            this.InitializeData();
            this.PaymentFrequency = 'monthly';
            setTimeout(() => {
                if (this.LoanTermInYears === 30) TokenService.GetTokenValue('30_FIXED_RATE_250K', this.updateInitialInterestRate);
                else TokenService.GetTokenValue('15_FIXED_RATE_250K', this.updateInitialInterestRate);
            }, 150);
            this.showPopup(false);
            this.PopperContent = '<div class="popup-content">Private Mortgage Insurance (PMI) protects the lender in the event of default and is required when the homeowner&apos;s equity falls below 20%. While your required PMI may vary, we assume a PMI rate of 0.625%.</div>';
        },
        methods: {
            restrictMortgageRateToMaxValue() {
                this.MortgageRate = this.unformat(this.MortgageRate) > 100 ? 100.000 : this.MortgageRate;
            },
            viewExpenses() {
                this.ViewExpenses = !this.ViewExpenses;
            },
            unformat(val) {
                return Accounting.unformat(val);
            },
            calculatePayment(val) {
                const payFreq = this.PaymentFrequency === 'bi-weekly'
                    ? 26
                    : this.PaymentFrequency === 'weekly'
                        ? 52
                        : 12;
                this.interestRate = val;
                this.MortgageAmount = this.unformat(this.HomePrice) - this.unformat(this.HomePrice) * (this.unformat(this.DownPayment) / this.unformat(this.HomePrice) * 100 / 100);
                this.OneTimeExpenses = this.unformat(this.HomePrice) * 3 / 100;
                this.DownPaymentExpenses = parseFloat(this.unformat(this.DownPayment)) + parseFloat(this.OneTimeExpenses);
                this.MonthlyInterestRatePerc = this.interestRate / 1200;
                this.oneTimeExpense = this.homePrice * (parseFloat(this.oneTimeExpenseRate) / 100);
                let percentValue = parseFloat(this.PropertyTaxesPercent).toFixed(2);
                percentValue = this.unformat(this.PropertyTaxesPercent) <= 100.00 ? percentValue : 100.00;
                this.propertyTaxesPerYear = this.unformat(this.HomePrice) * (this.unformat(percentValue) / 100);
                if (!this.IsPmiInclude) {
                    this.PmiPerc = 0;
                } else {
                    this.PmiPerc = parseFloat(this.pmiPerc);
                }
                this.PmiAmount = this.MortgageAmount * this.PmiPerc;
                this.AmortizationPeriodInMonths = this.LoanTermInYears * 12;
                this.MoPrincipalIntestrest = PaymentHelper.getMonthlyPrincipalAndIntreset(this.MortgageAmount, this.AmortizationPeriodInMonths, this.MonthlyInterestRatePerc);
                this.PropertyTaxesCurrency = this.propertyTaxesPerYear / 12;
                this.HomeInsuranceCurrency = this.unformat(this.HomeInsurance) / 12;
                this.PmiCurrency = this.PmiAmount / 12;
                this.TotalMoPayment = this.MoPrincipalIntestrest / Math.round(payFreq / 12) + this.MoExtraPayment + this.PropertyTaxesCurrency * 12 / payFreq + this.HomeInsuranceCurrency * 12 / payFreq + this.PmiCurrency * 12 / payFreq + this.HoaFees * 12 / payFreq;

                /* Monthly payment breakdown doughnut chart */
                this.chartData.labels = [];
                this.chartData.datasets[0].data = [];
                this.chartData.labels.push(`Principal & Interest ${Accounting.formatMoney(this.MoPrincipalIntestrest, { precision: 2 })}`);
                this.chartData.labels.push(`Property Taxes ${Accounting.formatMoney(this.PropertyTaxesCurrency, { precision: 2 })}`);
                this.chartData.labels.push(`Homeowner’s Insurance ${Accounting.formatMoney(this.HomeInsuranceCurrency, { precision: 2 })}`);
                this.chartData.labels.push(`HOA Fees ${Accounting.formatMoney(this.HoaFees, { precision: 2 })}`);

                const paymentPercent = Math.round(Math.abs(this.MoPrincipalIntestrest / this.TotalMoPayment * 100));
                const porpertyTxPercent = Math.round(Math.abs(this.PropertyTaxesCurrency / this.TotalMoPayment * 100));
                const insurancePercent = Math.round(Math.abs(this.HomeInsuranceCurrency / this.TotalMoPayment * 100));
                const hoaPercent = Math.round(Math.abs(this.HoaFees / this.TotalMoPayment * 100));

                this.chartData.datasets[0].data.push(paymentPercent);
                this.chartData.datasets[0].data.push(porpertyTxPercent);
                this.chartData.datasets[0].data.push(insurancePercent);
                this.chartData.datasets[0].data.push(hoaPercent);
                this.DownPayPercentage = this.DownPaymentPercent.toString().length >= 6 && this.DownPaymentPercent < 100 ? this.DownPaymentPercent.toFixed(2) : this.DownPaymentPercent;
                this.CheckPMI = !(this.DownPayPercentage >= 20);
                if (this.CheckPMI === false) this.IsPmiInclude = false;
                this.updateTableCalculation();
            },
            updateInitialInterestRate(val) {
                this.MortgageRate = val;
                this.calculatePayment(val);
            },
            updateMortgageRateValue(val) {
                this.MortgageRate = val;
            },
            updateMortgageRate(val) {
                if (val === 30) {
                    TokenService.GetTokenValue('30_FIXED_RATE_250K', this.updateMortgageRateValue);
                } else {
                    TokenService.GetTokenValue('15_FIXED_RATE_250K', this.updateMortgageRateValue);
                }
            },
            updateHomeInsurance() {
                const updatedHomeInsurance = this.unformat(this.HomePrice) * (this.HomeInsurancePerc / 100);
                this.HomeInsurance = Accounting.formatNumber(updatedHomeInsurance);
                this.DownPaymentPercentChange(this.unformat(this.DownPaymentPercent));
                this.PropertyTaxesPercentChange(this.unformat(this.PropertyTaxesPercent));
                if (this.unformat(this.InitialHomePrice) !== this.unformat(this.HomePrice)) this.InitialHomePrice = this.HomePrice;
            },
            currencyFilterWithPrecision(figure) {
                return figure > 0 ? Accounting.formatNumber(figure, { precision: 2 }) : 0;
            },
            InitializeData() {
                const updatedHomeInsurance = this.unformat(this.HomePrice) * (this.HomeInsurancePerc / 100);
                this.HomeInsurance = Accounting.formatNumber(updatedHomeInsurance);
                this.DownPaymentPercentChange(this.DownPaymentPercent);
                this.PropertyTaxesPercentChange(this.PropertyTaxesPercent);
                this.updateTableCalculation();
            },
            PropertyTaxesChange(value) {
                let propertyTaxesValue = this.unformat(value);
                if (propertyTaxesValue !== this.unformat(this.InitialPropertyTax)) {
                    if (propertyTaxesValue > this.unformat(this.HomePrice)) {
                        propertyTaxesValue = this.unformat(this.HomePrice);
                        this.PropertyTaxes = Accounting.formatNumber(propertyTaxesValue);
                    } else if (propertyTaxesValue > this.unformat(this.maxValue)) {
                        this.PropertyTaxes = this.maxValue;
                        propertyTaxesValue = this.maxValue;
                    }
                    propertyTaxesValue = parseFloat(propertyTaxesValue).toFixed(2);
                    this.PropertyTaxesPercent = this.getPercentageAmount(propertyTaxesValue);
                    this.InitialPropertyTaxPercent = this.PropertyTaxesPercent;
                    this.InitialPropertyTax = this.PropertyTaxes;
                }
            },
            PropertyTaxesPercentChange(value) {
                if (this.unformat(value) !== this.unformat(this.InitialPropertyTaxPercent) || this.unformat(this.InitialHomePrice) !== this.unformat(this.HomePrice)) {
                    if (this.unformat(this.propertyTaxesPercent) <= 100) {
                        const percentValue = parseFloat(value).toFixed(2);
                        this.PropertyTaxes = Accounting.formatNumber(this.getDollarAmount(percentValue));
                    }
                    if (this.unformat(this.PropertyTaxes) > this.unformat(this.HomePrice)) {
                        this.PropertyTaxes = Accounting.formatNumber(this.HomePrice);
                    }
                    this.InitialPropertyTax = this.PropertyTaxes;
                    this.InitialPropertyTaxPercent = this.PropertyTaxesPercent;
                }
            },
            DownPaymentChange(value) {
                let downPaymentValue = this.unformat(value);
                if (downPaymentValue !== this.unformat(this.InitialDownPayment)) {
                    if (downPaymentValue > this.unformat(this.HomePrice)) {
                        downPaymentValue = this.unformat(this.HomePrice);
                        this.DownPayment = Accounting.formatNumber(downPaymentValue);
                    } else if (downPaymentValue > this.unformat(this.maxValue)) {
                        this.DownPayment = this.maxValue;
                        downPaymentValue = this.maxValue;
                    }
                    downPaymentValue = parseFloat(downPaymentValue).toFixed(2);
                    this.DownPaymentPercent = this.getPercentageAmount(downPaymentValue);
                    this.InitialDownPaymentPercent = this.DownPaymentPercent;
                    this.InitialDownPayment = this.DownPayment;
                }
            },
            DownPaymentPercentChange(value) {
                if (this.unformat(value) !== this.unformat(this.InitialDownPaymentPercent) || this.unformat(this.InitialHomePrice) !== this.unformat(this.HomePrice)) {
                    if (this.unformat(this.downPaymentPercent) <= 100) {
                        const percentValue = parseFloat(value).toFixed(2);
                        this.DownPayment = Accounting.formatNumber(this.getDollarAmount(percentValue));
                    }
                    if (this.unformat(this.DownPayment) > this.unformat(this.HomePrice)) {
                        this.DownPayment = Accounting.formatNumber(this.HomePrice);
                    }
                    this.InitialDownPaymentPercent = this.DownPaymentPercent;
                    this.InitialDownPayment = this.DownPayment;
                }
            },
            getDollarAmount(value) {
                let calculatedValue = this.unformat(value) / 100 * this.unformat(this.HomePrice);
                calculatedValue = calculatedValue.toFixed(2) % 1 === 0 ? Math.round(calculatedValue) : calculatedValue.toFixed(2);
                return isNaN(calculatedValue) || calculatedValue === 'Infinity' ? 0 : calculatedValue;
            },
            getPercentageAmount(value) {
                const calculatedValue = this.unformat(value) / this.unformat(this.HomePrice) * 100;
                return isNaN(calculatedValue) || calculatedValue === 'Infinity' ? 0 : calculatedValue.toFixed(2);
            },
            showPopup(data) {
                this.ShowModal = data;
                document.body.style.WebkitOverflowScrolling = 'touch';
                document.body.style.position = 'relative';
            },
            updateTableCalculation() {
                const PmiIncludeVal = this.IsPmiInclude ? 0 : 1;
                const data = AmortizationBreakdownHelper.amortizationBreakdown(this.MortgageAmount, this.LoanTermInYears, this.interestRate, this.HomePrice, this.calculateTaxesFees, 0, PmiIncludeVal, this.CurrentDate.toString(), this.PayFreqInNumber);
                this.AllTotalInterest = data.AllTotalInterest;
                this.AllTotalPrincipal = data.AllTotalPrincipal;
                this.AllTotalTaxesFees = data.AllTotalTaxesFees;
                this.AllTotalExtraPayments = data.AllTotalExtraPayments;
            },
        },
    };
</script>
<style src="../../assets/styles/common.scss" lang="scss"></style>
<style src="./main.scss" lang="scss"></style>
